import { useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";

import { Chip } from "@/components/Chip";
import { DataTable, type DataCol } from "@/components/DataTable";
import { PageHead } from "@/components/PageHead";
import { Select } from "@/components/inputs";
import {
  REF_TYPE_LABEL, useAllocations, useCompanyFiles, useReferenceOverrides, useToolVersions,
  type Allocation, type CompanyFile, type ReferenceOverride, type ToolVersionRow,
} from "@/modules/organisations/detail_fixtures";
import { ROLE_LABEL, useOrganisations, useOrgUsers } from "@/modules/organisations/fixtures";

/** Organisation detail — the provisions behind Fabric's 35 `companies` routes.
 *
 *  STAGE A: Fabric's shapes, unchanged. Spaces, allocations, filename-identified
 *  files and the archive lifecycle all come across as they are. Every one of
 *  them is on the stage-C list (TODO_FABRIC.md, THE GATE), and none of them is
 *  redesigned here — porting and redesigning at once means a failure has two
 *  possible causes.
 *
 *  Routes covered, by tab:
 *    Products & spaces  manage-products · allocate/revoke product ·
 *                       allocate/deallocate space · private-spaces create/edit ·
 *                       delete-private · bulk-apply · permissions view/edit
 *    Users              manage-users
 *    Files              13 routes: company-file upload/download/archive/revive/
 *                       permadelete, and reference-override upload/download/
 *                       reset/revive/permadelete
 *    Tool versions      tool-versions · tool-versions/export.xlsx
 *  Plus deactivate/reactivate, on the header.
 */

type Tab = "products" | "users" | "files" | "tools";

const FILE_COLS: DataCol<CompanyFile>[] = [
  { key: "name", label: "Filename", width: 230, mono: true, render: (f) => f.filename },
  { key: "kind", label: "Kind", width: 90, render: (f) => <Chip tone={f.kind === "firmware" ? "info" : "mute"}>{f.kind}</Chip> },
  { key: "size", label: "Size", width: 90, render: (f) => `${(f.size_bytes / 1024).toFixed(0)} KB` },
  { key: "by", label: "Uploaded by", width: 190, render: (f) => f.uploaded_by },
  { key: "at", label: "Uploaded", width: 100, render: (f) => f.uploaded_at },
  {
    key: "act", label: "", width: 210,
    render: (f) => (
      <span style={{ display: "flex", gap: 6 }}>
        <button type="button" className="btn ghost sm">Download</button>
        {f.archived
          ? <>
              <button type="button" className="btn ghost sm">Revive</button>
              <button type="button" className="btn ghost sm">Delete</button>
            </>
          : <button type="button" className="btn ghost sm">Archive</button>}
      </span>
    ),
  },
];

const TOOL_COLS: DataCol<ToolVersionRow>[] = [
  { key: "who", label: "Person", width: 210, mono: true, render: (r) => r.login_id },
  { key: "tool", label: "Tool", width: 110, render: (r) => r.tool },
  { key: "plat", label: "Platform", width: 90, render: (r) => r.platform },
  {
    key: "ver", label: "Version", width: 120, mono: true,
    render: (r) => r.below_floor
      ? <span style={{ color: "var(--bad)" }}>{r.version_name} ↓</span>
      : r.version_name,
  },
  { key: "seen", label: "Last seen", width: 150, render: (r) => r.last_seen },
  {
    key: "state", label: "", width: 130,
    render: (r) => r.below_floor
      ? <Chip tone="bad">below floor</Chip>
      : <Chip tone="ok">up to date</Chip>,
  },
];

export function OrganisationDetailPage() {
  const nav = useNavigate();
  const { orgId = "" } = useParams();
  const orgs = useOrganisations();
  const org = orgs.find((o) => o.organisation_id === orgId) ?? orgs[0];
  const users = useOrgUsers().filter((u) => u.organisation_id === org.organisation_id);
  const allocations = useAllocations(org.organisation_id);
  const toolRows = useToolVersions(org.organisation_id);

  const [tab, setTab] = useState<Tab>("products");
  const [cps, setCps] = useState<number>(allocations[0]?.cps_id ?? 0);
  const files = useCompanyFiles(cps);
  const refs = useReferenceOverrides(cps);

  const allocCols = useMemo<DataCol<Allocation>[]>(() => [
    { key: "product", label: "Product", width: 180, render: (a) => a.product },
    {
      key: "space", label: "Space", width: 180,
      render: (a) => (
        <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {a.space}{a.private && <Chip tone="warn">private</Chip>}
        </span>
      ),
    },
    { key: "users", label: "Users with access", width: 130, render: (a) => a.users_with_access || "—" },
    { key: "on", label: "Enabled", width: 90, render: (a) => a.enabled ? <Chip tone="ok">on</Chip> : <Chip tone="mute">off</Chip> },
    {
      key: "act", label: "", width: 300,
      render: (a) => (
        <span style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button type="button" className="btn ghost sm">Permissions</button>
          <button type="button" className="btn ghost sm" onClick={() => { setCps(a.cps_id); setTab("files"); }}>Files</button>
          {a.private && <button type="button" className="btn ghost sm">Edit</button>}
          <button type="button" className="btn ghost sm">Deallocate</button>
        </span>
      ),
    },
  ], []);

  return (
    <div className="mpage on">
      <div style={{ fontSize: 11.5, marginBottom: 6 }}>
        <Link to="/organisations/organisations">← All organisations</Link>
      </div>
      <PageHead
        eyebrow={`Organisations · ${org.organisation_id}`}
        title={org.name}
        desc={org.description ?? undefined}
        right={
          <span style={{ display: "flex", gap: 8 }}>
            <button type="button" className="btn ghost">
              {org.status === "active" ? "Deactivate" : "Reactivate"}
            </button>
            <button type="button" className="btn teal">Allocate product</button>
          </span>
        }
      />

      <div style={{ display: "flex", gap: 2, marginBottom: 14, borderBottom: "1px solid var(--line)" }}>
        {([["products", "Products & spaces"], ["users", "Users"], ["files", "Files"], ["tools", "Tool versions"]] as [Tab, string][])
          .map(([k, label]) => (
            <button key={k} type="button" onClick={() => setTab(k)} aria-pressed={tab === k}
              className={tab === k ? "btn sm on" : "btn ghost sm"}>{label}</button>
          ))}
      </div>

      {tab === "products" && (
        <>
          <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
            <button type="button" className="btn ghost sm">Add private space</button>
            <button type="button" className="btn ghost sm">Bulk-apply default spaces</button>
          </div>
          <DataTable rows={allocations} cols={allocCols} rowKey={(a) => a.cps_id}
            empty={<div className="panel panel-pad cap">No products allocated to this organisation yet.</div>} />
        </>
      )}

      {tab === "users" && (
        <DataTable
          rows={users}
          cols={[
            { key: "name", label: "Person", width: 180, render: (u) => u.name },
            { key: "email", label: "Email", width: 220, mono: true, render: (u) => u.login_id },
            { key: "role", label: "Role", width: 140, render: (u) => ROLE_LABEL[u.role] },
            { key: "access", label: "Space access", width: 220,
              render: (u) => u.space_access.map((s) => s.space).join(" · ") || <span className="cap">none</span> },
            { key: "mfa", label: "MFA", width: 70, render: (u) => u.mfa ? <Chip tone="ok">on</Chip> : <Chip tone="warn">off</Chip> },
          ]}
          rowKey={(u) => u.user_id}
          onRowClick={() => nav("/organisations/users")}
          empty={<div className="panel panel-pad cap">Nobody in this organisation yet.</div>} />
      )}

      {tab === "files" && (
        <>
          <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
            <span className="cap">Allocation</span>
            <Select value={String(cps)} onChange={(e) => setCps(Number(e.target.value))} style={{ minWidth: 260 }}>
              {allocations.map((a) => (
                <option key={a.cps_id} value={a.cps_id}>{a.product} · {a.space}</option>
              ))}
            </Select>
            <button type="button" className="btn teal sm">Upload file</button>
          </div>

          <div className="section-label">Reference files — company overrides</div>
          <div className="panel panel-pad" style={{ marginBottom: 16 }}>
            {refs.map((r: ReferenceOverride) => (
              <div key={r.ref_type} style={{ display: "flex", gap: 10, alignItems: "center", padding: "7px 0" }}>
                <span style={{ width: 200, fontSize: 12.5 }}>{REF_TYPE_LABEL[r.ref_type]}</span>
                <span className="mono" style={{ flexGrow: 1, fontSize: 12 }}>{r.filename}</span>
                {r.overridden
                  ? <Chip tone="warn">company override</Chip>
                  : <Chip tone="mute">product default</Chip>}
                <button type="button" className="btn ghost sm">Download</button>
                <button type="button" className="btn ghost sm">Upload override</button>
                {r.overridden && <button type="button" className="btn ghost sm">↺ Reset override</button>}
              </div>
            ))}
          </div>

          <div className="section-label">Firmware &amp; config library</div>
          <DataTable rows={files} cols={FILE_COLS} rowKey={(f) => f.file_id}
            empty={<div className="panel panel-pad cap">No files for this allocation.</div>} />
          <div className="cap" style={{ marginTop: 8 }}>
            A filename is unique per allocation and kind, <strong>including archived rows</strong> — an archived
            file blocks re-upload of that name until it is revived or permanently deleted, so a new build never
            silently shadows an old one.
          </div>
        </>
      )}

      {tab === "tools" && (
        <>
          <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
            <button type="button" className="btn ghost sm">Export .xlsx</button>
            <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--t-mute)", alignSelf: "center" }}>
              {toolRows.filter((r) => r.below_floor).length} below the rollout floor
            </span>
          </div>
          <DataTable rows={toolRows} cols={TOOL_COLS} rowKey={(r) => r.login_id}
            empty={<div className="panel panel-pad cap">No tool activity recorded.</div>} />
        </>
      )}
    </div>
  );
}
